import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "../../assets/css/mes-annonces/MesAnnoncesVisualisation.css";
import useArticles from "../../hooks/useArticle";
import { formatPrice } from "../../utils/formatPrice";
import { imageArticleUrl } from "../../utils/modules";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { truncateText } from "../../utils/formatTexte";
import LoaderArticleVisualisation from "../../components/loader/article/LoaderArticleVisualisation";

import ArticleInfo from "../../components/article/ArticleInfo";
import ArticleDashboard from "../../components/article/ArticleDashboard";

export default function VisualisationAnnonce({ setAlert }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const stepSelected = searchParams.get("step") || 1;
  const navigate = useNavigate();
  const [article, setArticle] = useState();
  const [loadingOne, setLoadingOne] = useState(false);
  const { findOneArticle } = useArticles();
  const [step, setStep] = useState(+(stepSelected));
  // Carousel state
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [imgVersion, setImgVersion] = useState(Date.now());


  // Helpers image (identique à MesAnnonces)
  const formatPhotoPath = (p) => (p || "").replace(/\\/g, "/");
  const buildPhotoUrl = (storedPath) => {
    const p = formatPhotoPath(storedPath);
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;

    const idx = p.indexOf("files/");
    const relative = idx !== -1 ? p.slice(idx) : p;
    return `${imageArticleUrl}/${relative}?v=${imgVersion}`;
  };

  useEffect(() => {
    if (!id) return;

    setLoadingOne(true); // IMPORTANT : sinon le loader ne s'affiche jamais
    findOneArticle(id)
      .then((res) => {
        if (res?.data?.data) {
          console.log(res.data.data);
          
          setArticle(res.data.data);
          setImgVersion(Date.now());
        } else {
          setArticle(undefined);
        }
        setLoadingOne(false);
      })
      .catch((err) => {
        setLoadingOne(false);
        setAlert?.({
          message:
            "Une erreur s'est produite lors de la récupération des données de l'annonce.",
          type: "danger",
        });
        console.error("Erreur lors de la récupération de l'article :", err);
      });
  }, [id]);

  const images = useMemo(() => {
    const pics = article?.photos || [];
    return pics.map(buildPhotoUrl).filter(Boolean);
  }, [article, imgVersion]);

  const useCarousel = images.length > 1;

  const updateNavState = () => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft < max - 2);
  };

  useEffect(() => {
    if (!useCarousel) return;
    const el = trackRef.current;
    if (!el) return;

    updateNavState();

    const onScroll = () => updateNavState();
    const onResize = () => updateNavState();

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [useCarousel, images.length]);

  const scrollByOneCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;

    const firstItem = el.querySelector(".vaCarouselItem");
    const cardWidth = firstItem
      ? firstItem.getBoundingClientRect().width
      : el.clientWidth;

    const gap = 18;
    const delta = dir * (cardWidth + gap);
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Breadcrumb
  const breadcrumbItems = useMemo(() => {
    const title = article?.titre
      ? `${truncateText(article.titre, 18)}`
      : "Visualisation";

    return step == 1
      ? [
          { label: "Accueil", link: "/" },
          { label: "Mon compte", link: "/utilisateur/compte" },
          { label: "Mes annonces", link: "/utilisateur/mesAnnonces" },
          { label: title, link: `/article/visualisation/${id}` },
        ]
      : step == 2
        ? [
            { label: "Accueil", link: "/" },
            { label: "Mon compte", link: "/utilisateur/compte" },
            { label: "Mes annonces", link: "/utilisateur/mesAnnonces" },
            { label: title, fonction: () => setStep(1) },
            { label: "Dashboard" },
          ]
        : [];
  }, [article?.titre, step]);

  if (loadingOne) {
    return (
      <div className="vaPage">
        <div className="container">
          <div className="vaBreadcrumbWrap">
            <Breadcrumb
              items={[
                { label: "Accueil", link: "/" },
                { label: "Mon compte", link: "/utilisateur/compte" },
                { label: "Mes annonces", link: "/utilisateur/mesAnnonces" },
                { label: "Chargement..." },
              ]}
            />
          </div>

          <div style={{ padding: 12 }}>
            <LoaderArticleVisualisation />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="vaPage">
        <div className="container">
          <div className="vaBreadcrumbWrap">
            <Breadcrumb
              items={[
                { label: "Accueil", link: "/" },
                { label: "Mon compte", link: "/utilisateur/compte" },
                { label: "Mes annonces", link: "/utilisateur/mesAnnonces" },
                { label: "Introuvable" },
              ]}
            />
          </div>
          <div style={{ padding: 18 }}>Annonce introuvable.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="vaPage">
      <div className="container">
        <div className="vaBreadcrumbWrap">
          <Breadcrumb items={breadcrumbItems} />
        </div>
        {step === 1 && (
          <ArticleInfo
            images={images}
            useCarousel={useCarousel}
            scrollByOneCard={scrollByOneCard}
            canLeft={canLeft}
            trackRef={trackRef}
            canRight={canRight}
            article={article}
            navigate={navigate}
            formatPrice={formatPrice}
            showDashboard={() => {
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <ArticleDashboard
            article={article}
            setArticle={setArticle}
            showInfo={() => {
              setStep(1);
            }}
            formatPrice={formatPrice}
            setAlert={setAlert}
          />
        )}
      </div>
    </div>
  );
}
